/** SaaS Backside Sidebar — Star Crystal V brand mark
 * (VISTOERA_STAR_CRYSTAL_3D_V_GLOBAL_MARK_CANVAS_RETURN_AND_GLOBAL_SCROLLBAR_P0_V1,
 * VISTOERA_CANVAS_ACCOUNT_MENU_EXIT_TRANSITIONS_WORKSPACE_RETURN_CONTEXT_AND_SHELL_V_LOGO_CANVAS_BACK_NAVIGATION_P0_V1
 * §20-§23, VistoeraTASK-00550/00551/00552/00573 — unified V + VISTOERA click target).
 *
 * With active Canvas Return Context: ALWAYS resume the Canvas the user
 * left — on Overview, Profile, Plan Usage, Content Production, or any
 * other Tenant Backoffice route — via `openCanvasFromWorkspace`.
 *
 * Without context / deleted project / a project that is not the signed-in
 * user's own: enter Canvas via `/home` (own last-opened → own Home Canvas).
 * Logo is a global Canvas entry — never refuse navigation with
 * "没有可返回的画布" (VistoeraTASK-00573), and never resumes a teammate's
 * project (VistoeraTASK-00577). */
import { Tooltip } from 'antd'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import VistoeraVLogo from '../../components/VistoeraVLogo'
import { getContentProject } from '../api'
import { useAuth } from '../AuthContext'
import { readCanvasNavigationContext } from '../navigation/canvasNavigationContext'
import { resolveValidatedVLogoDestination } from '../navigation/canvasVLogoNavigation'
import { openCanvasFromWorkspace } from '../workspace/canvasWorkspaceTransition'

interface Props {
  workspaceId: string
  /** When true, renders the VISTOERA wordmark inside the same click target as the V icon. */
  showBrandTitle?: boolean
}

export default function SaasBacksideBrandLogo({ workspaceId, showBrandTitle = false }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('navigation')
  const [navigating, setNavigating] = useState(false)
  const { me } = useAuth()
  const userId = me?.user.id ?? null

  const hasCanvasContext = Boolean(readCanvasNavigationContext(workspaceId)?.contentProjectId)

  const onClick = useCallback(async () => {
    if (navigating) return
    setNavigating(true)
    try {
      // VistoeraTASK-00577: personal-canvas entry — only the signed-in user's OWN
      // project is resumed; never a teammate's project the user merely may view.
      const dest = await resolveValidatedVLogoDestination(workspaceId, location.pathname, getContentProject, {
        userId,
      })
      openCanvasFromWorkspace(navigate, dest.route)
    } finally {
      setNavigating(false)
    }
  }, [navigate, navigating, workspaceId, location.pathname, userId])

  const tooltip = hasCanvasContext ? t('myCanvas.tooltip') : t('myVistoera.tooltip')
  const ariaLabel = hasCanvasContext ? t('myCanvas.ariaLabel') : t('myVistoera.ariaLabel')

  const btnClass = showBrandTitle ? 'tc-brand-home-btn' : 'tc-brand-logo-btn'

  const brandButton = (
    <button
      type="button"
      className={btnClass}
      onClick={() => void onClick()}
      disabled={navigating}
      aria-label={ariaLabel}
    >
      <VistoeraVLogo className="tc-brand-logo-mark" />
      {showBrandTitle ? <span className="tc-brand-title">VISTOERA</span> : null}
    </button>
  )

  if (!showBrandTitle) {
    return (
      <Tooltip title={tooltip} placement="right" mouseEnterDelay={0.35}>
        {brandButton}
      </Tooltip>
    )
  }

  return (
    <div className="tc-brand-logo-host">
      <Tooltip title={tooltip} placement="right" mouseEnterDelay={0.35}>
        {brandButton}
      </Tooltip>
    </div>
  )
}
